import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';

import { useAuth } from 'contexts/AuthContext';

import SecurityCheckStatusLabelChip from 'components/chip/SecurityCheckStatusLabelChip';
import Circular from 'components/common/CustomCircular';
import { Condition, Filter } from 'components/condition-list/SearchFilter';
import {
  ConsoleColumn,
  ConsoleDetailDrawer,
  ConsoleHeader,
  ConsoleSearchFilter,
  ConsoleTable,
} from 'components/console';
import {
  FinancialYearResult,
  ListFinancialYearResponse,
  listFinancialYear,
} from 'stores/FinancialYearStoreAdapter';
import {
  SecurityCheckDetailResult,
  SecurityCheckListResult,
  getSecurityCheckDetail,
  listSecurityCheckAdmin,
} from 'stores/SecurityCheckStoreAdapter';
import { errorDefault } from 'utils/Toastify';

import SecurityCheckAdminDetail from './components/SecurityCheckAdminDetail';

const SecurityCheckManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const { userSession } = useAuth();
  const [searchParams] = useSearchParams();
  const [isProgress, setProgress] = useState(true);
  const [items, setItems] = useState<SecurityCheckListResult[]>([]);
  const [selectedItem, setSelectedItem] = useState<SecurityCheckListResult | null>(null);
  const [detail, setDetail] = useState<SecurityCheckDetailResult | null>(null);
  const [financialYears, setFinancialYears] = useState<FinancialYearResult[]>([]);
  const [currentYear, setCurrentYear] = useState<number | null>(null);
  // 現在一覧に適用中の年度（更新後・再読み込みで同じ年度を再検索するため保持）
  const [selectedYear, setSelectedYear] = useState<number | null>(null);

  // クエリパラメータで直接詳細を開く
  const directFinancialYear = searchParams.get('financialYear');
  const directMonth = searchParams.get('month');

  useEffect(() => {
    listFinancialYear()
      .then((res: ListFinancialYearResponse) => {
        setFinancialYears(res.results);
        const current = res.current?.financialYear || (res.results[0]?.financialYear ?? null);
        setCurrentYear(current);

        if (directFinancialYear && directMonth) {
          const fy = parseInt(directFinancialYear);
          const m = parseInt(directMonth);
          fetchDetail(fy, m);
          fetchList(fy);
        } else if (directFinancialYear) {
          // 年度のみ指定: その年度の一覧を初期表示
          fetchList(parseInt(directFinancialYear));
        } else if (current) {
          fetchList(current);
        }
      })
      .catch(() => {
        errorDefault('年度情報の取得に失敗しました');
        setProgress(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchList = (financialYear: number) => {
    setSelectedYear(financialYear);
    setProgress(true);
    listSecurityCheckAdmin({ financialYear })
      .then((result) => {
        setItems(result);
        setProgress(false);
      })
      .catch(() => {
        setProgress(false);
        errorDefault('セキュリティチェック一覧の取得に失敗しました');
      });
  };

  // 現在適用中の年度で再検索する（未設定なら現在年度）
  const refetch = () => {
    const year = selectedYear ?? currentYear;
    if (year) fetchList(year);
  };

  const fetchDetail = (financialYear: number, month: number) => {
    setProgress(true);
    getSecurityCheckDetail({ financialYear, month })
      .then((result) => {
        setDetail(result);
        setProgress(false);
      })
      .catch(() => {
        setProgress(false);
        errorDefault('セキュリティチェック詳細の取得に失敗しました');
      });
  };

  const handleSearch = (filters: Filter[]) => {
    setSelectedItem(null);
    setDetail(null);
    const yearFilter = filters.find((f) => f.key === 'financialYear');
    const year = yearFilter ? parseInt(yearFilter.value.key) : currentYear;
    if (year) {
      fetchList(year);
    }
  };

  const handleRowClick = (row: SecurityCheckListResult) => {
    setSelectedItem(row);
    fetchDetail(row.financialYear, row.month);
  };

  const handleRefresh = () => {
    if (selectedItem) {
      fetchDetail(selectedItem.financialYear, selectedItem.month);
    }
    refetch();
  };

  const conditions: Condition[] = useMemo(() => {
    return [
      {
        key: 'financialYear',
        name: '年度',
        type: 'financial-year-select' as const,
        color: '#78C1F3',
        financialYears: financialYears,
      },
    ];
  }, [financialYears]);

  const initialFilters: Filter[] = useMemo(() => {
    // クエリパラメータの年度があれば優先、なければ現在年度
    const year = directFinancialYear ? parseInt(directFinancialYear) : currentYear;
    if (!year) return [];
    return [
      {
        id: 'financialYear-init',
        key: 'financialYear',
        color: '#78C1F3',
        value: { key: String(year), name: `年度: ${year}期` },
      },
    ];
  }, [currentYear, directFinancialYear]);

  const columns: ConsoleColumn<SecurityCheckListResult>[] = [
    {
      key: 'period',
      header: '対象月度',
      render: (i) => (
        <Box component="span" sx={{ fontWeight: 600 }}>
          {i.financialYear}期 {i.month}月度
        </Box>
      ),
    },
    {
      key: 'monthly',
      header: '月次チェック',
      width: 140,
      render: (i) => <SecurityCheckStatusLabelChip status={i.monthlyStatus} />,
    },
    {
      key: 'pcPatch',
      header: 'PCパッチ適用',
      width: 140,
      render: (i) => <SecurityCheckStatusLabelChip status={i.pcPatchStatus} />,
    },
  ];

  // クエリパラメータで直接詳細表示の場合はテーブルなし（別タブ想定・閉じる導線を表示）
  if (directFinancialYear && directMonth && detail) {
    const fy = parseInt(directFinancialYear);
    const m = parseInt(directMonth);
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Stack
          direction="row"
          alignItems="center"
          sx={{ px: 1, py: 0.5, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}
        >
          <Button
            size="small"
            startIcon={<ArrowBackIcon sx={{ fontSize: 18 }} />}
            onClick={() => window.close()}
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            閉じる
          </Button>
        </Stack>
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <SecurityCheckAdminDetail
            financialYear={fy}
            month={m}
            detail={detail}
            userSession={userSession}
            onRefresh={handleRefresh}
          />
        </Box>
        <Circular open={isProgress} />
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', overflow: 'hidden' }}>
      <ConsoleHeader
        title="セキュリティチェック管理"
        count={items.length}
        description="年度ごとの月次・PCパッチ適用チェックの実施状況を確認します"
        backLabel="セキュリティ"
        onBack={() => navigate('/security')}
        onRefresh={refetch}
      />

      <ConsoleTable
        columns={columns}
        rows={items}
        rowKey={(i) => `${i.financialYear}-${i.month}`}
        selectedKey={
          selectedItem ? `${selectedItem.financialYear}-${selectedItem.month}` : undefined
        }
        onRowClick={handleRowClick}
        emptyMessage="対象年度のデータがありません。年度を選択してください。"
        toolbar={
          <ConsoleSearchFilter
            conditions={conditions}
            initialFilters={initialFilters}
            onFiltersChange={handleSearch}
            conditionKey="security-check-management"
            userProfileKey="security-check-management-list-search"
          />
        }
      />

      <ConsoleDetailDrawer
        open={selectedItem !== null && detail !== null}
        onClose={() => {
          setSelectedItem(null);
          setDetail(null);
        }}
        title={selectedItem ? `${selectedItem.financialYear}期 ${selectedItem.month}月度` : ''}
        width={860}
      >
        {selectedItem && detail && (
          <Box sx={{ mx: -2.5, mt: -2.5 }}>
            <SecurityCheckAdminDetail
              financialYear={selectedItem.financialYear}
              month={selectedItem.month}
              detail={detail}
              userSession={userSession}
              onRefresh={handleRefresh}
            />
          </Box>
        )}
      </ConsoleDetailDrawer>

      <Circular open={isProgress} />
    </Box>
  );
};

export default SecurityCheckManagementPage;
